import { layoutToWorld } from '../../core/coords';
import type { PolygonWithHoles, Ring, Vec2, Vec3 } from '../../geo/types';
import { difference, inflate, intersection } from '../../geo/clip';
import { clipCandidates } from '../../geo/clipCandidates';
import { polygonArea } from '../../geo/polygon';
import { createGroundElevation } from '../../site/groundElevation';
import { createDensifyConfig } from '../terrain/densify';
import { withHeightRefinement } from '../terrain/heightRefinement';
import { WORLD } from '../../core/config';
import { edgeIdFor, SURFACE } from '../mesh/attributes';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { BuildContext, Obstacle } from '../types';
import { orientedFace } from './solids';
import { footprintInside } from './lotFootprint';
import { HOGE_HUIS, planHogeHuis } from './surveyed/hogeHuisPlan';
import { lotReservations } from './fabricLots';

export interface HogeHuisYardPlan {
  readonly plan: ReturnType<typeof planHogeHuis>;
  readonly ground: readonly PolygonWithHoles[];
}

type YardContext = Pick<BuildContext, 'layout' | 'site'> & {
  readonly hogeHuis?: HogeHuisYardPlan | undefined;
};

export type StandingTimber = (typeof HOGE_HUIS.timber)[number];

const preparedPlans = new WeakMap<object, ReturnType<typeof planHogeHuis>>();
/** One immutable feature context owns the timber, yard skin and tree keep-out checks. */
function preparedPlan(ctx: YardContext) {
  let plan = preparedPlans.get(ctx);
  if (!plan) {
    plan = planHogeHuis(ctx, ctx.site.buildableIndex, lotReservations(ctx.layout));
    preparedPlans.set(ctx, plan);
  }
  return plan;
}

/** Includes the whole leaning board, rather than just its narrow contact with the ground. */
export function timberFootprint(item: StandingTimber): Ring {
  const c = Math.cos(item.yaw),
    s = Math.sin(item.yaw);
  return [
    [-item.width / 2, -item.depth / 2],
    [item.width / 2, -item.depth / 2],
    [item.width / 2, item.depth / 2],
    [-item.width / 2, item.depth / 2],
  ].map(([u, v]): Vec2 => [item.at[0]! + u! * c - v! * s, item.at[1]! + u! * s + v! * c]);
}

/** Long timber is conspicuous in both period images; individual counts and rack sizes are estimates. */
export function buildStandingTimber(
  builder: MeshBuilder,
  item: StandingTimber,
  style: VertexStyle,
  ground: number
): void {
  const frame = { origin: layoutToWorld([item.at[0]!, item.at[1]!], ground), yaw: item.yaw };
  const pitch = item.width / item.boards,
    width = pitch * 0.84,
    thickness = 0.1;
  for (const sign of [-1, 1]) {
    for (let i = 0; i < item.boards; i++) {
      const u = -item.width / 2 + pitch * (i + 0.5),
        height = item.height - (i % 3) * 0.16,
        foot = sign * (item.depth / 2 - thickness),
        top = sign * 0.15;
      const pts = (y: number, v: number): Vec3[] => [
        [u - width / 2, y, -v - thickness / 2],
        [u + width / 2, y, -v - thickness / 2],
        [u + width / 2, y, -v + thickness / 2],
        [u - width / 2, y, -v + thickness / 2],
      ];
      const bottom = pts(0, foot),
        upper = pts(height, top);
      for (let j = 0; j < 4; j++) {
        const k = (j + 1) % 4;
        const outward: Vec3 = [
          (bottom[j]![0] + bottom[k]![0]) / 2 - u,
          0,
          (bottom[j]![2] + bottom[k]![2]) / 2 + foot,
        ];
        orientedFace(
          builder,
          style,
          frame,
          [bottom[j]!, bottom[k]!, upper[k]!, upper[j]!],
          outward
        );
      }
      orientedFace(builder, style, frame, upper, [0, 1, 0]);
      orientedFace(builder, style, frame, bottom, [0, -1, 0]);
    }
  }
}

export function placedHogeHuisTimber(ctx: YardContext): readonly StandingTimber[] {
  const planned = preparedPlan(ctx);
  if (!HOGE_HUIS.houses.every((h) => planned.lots.some((l) => l.reconstruction?.id === h.id)))
    return [];
  const yards = planned.reserved
    .filter((r) => r.id.startsWith('hoge-huis-timber-yard-'))
    .map((r) => r.polygon);
  return HOGE_HUIS.timber.filter((item) => {
    const foot = timberFootprint(item),
      poly = { outer: foot, holes: [] };
    return (
      footprintInside(ctx.site.buildableIndex, foot) &&
      !difference([poly], clipCandidates([poly], yards)).some((p) => polygonArea(p) > 0.001) &&
      !intersection([poly], clipCandidates([poly], [...ctx.site.streets, ...ctx.site.water])).some(
        (p) => polygonArea(p) > 0.001
      )
    );
  });
}

/** Built with civic objects so the tree pass respects the working timber yard. */
export function buildHogeHuisYard(ctx: BuildContext) {
  if (!HOGE_HUIS.houses.every((h) => ctx.lots.houses.some((l) => l.reconstruction?.id === h.id)))
    return { triangles: 0, obstacles: [], stacks: 0 };
  const elevation = createGroundElevation(ctx.layout),
    items = placedHogeHuisTimber(ctx);
  let triangles = 0;
  const obstacles: Obstacle[] = items.map((item, i) => {
    const foot = timberFootprint(item),
      ground = Math.max(...foot.map(elevation)),
      builder = ctx.chunks.builderAt('infrastructure', [item.at[0]!, item.at[1]!]),
      before = builder.triangleCount;
    buildStandingTimber(
      builder,
      item,
      {
        color: ctx.palette.shipHull.median.linear,
        surface: SURFACE.wood,
        edgeId: edgeIdFor(56_000 + i),
        strokeAngle: 0,
        extent: [0, 0],
      },
      ground
    );
    triangles += builder.triangleCount - before;
    return { id: item.id, kind: 'structure', polygon: foot };
  });
  triangles += buildHogeHuisGround(ctx);
  return { triangles, obstacles, stacks: items.length };
}

/** Keep the working storage ground clear of generated garden trees; this does not block walking. */
export function hogeHuisTreeObstacles(ctx: YardContext): readonly Obstacle[] {
  const planned = preparedPlan(ctx);
  return planned.reserved
    .filter((r) => r.id.startsWith('hoge-huis-timber-yard-'))
    .map((r) => ({
      id: `tree-keep-out:${r.id}`,
      kind: 'structure',
      polygon: r.polygon.outer,
    }));
}

/** A bare-earth interpretation of the working yard; its paving and soil are not measured in 1538. */
export function hogeHuisGroundPolygons(
  ctx: YardContext & Pick<BuildContext, 'paving'>
): readonly PolygonWithHoles[] {
  if (ctx.hogeHuis) return ctx.hogeHuis.ground;
  const planned = preparedPlan(ctx);
  const yards = planned.reserved
    .filter((r) => r.id.startsWith('hoge-huis-timber-yard-'))
    .map((r) => r.polygon);
  // Use the assembled paving, including its fitted outlines, rather than painting over quay edges.
  return difference(
    yards,
    inflate(
      ctx.paving.pieces.map((p) => p.polygon),
      0.001,
      'miter'
    )
  );
}

/** A colour surface follows the walking ground; it does not create a new terrain level. */
export function buildHogeHuisGround(ctx: BuildContext): number {
  const ground = withHeightRefinement(
    {
      ...createDensifyConfig(ctx.painterView, WORLD.groundY),
      triangulations: ctx.terrainPlans?.ground,
    },
    (p) => ctx.site.queries.groundHeightAt(p) + 0.015
  );
  let triangles = 0;
  for (const polygon of hogeHuisGroundPolygons(ctx)) {
    const builder = ctx.chunks.builderAt('infrastructure', polygon.outer[0]!),
      before = builder.triangleCount;
    builder.groundPolygon(
      {
        color: ctx.palette.streetGround.median.linear,
        surface: SURFACE.quayEarth,
        edgeId: edgeIdFor(56_010),
        strokeAngle: 0,
        extent: [0, 0],
      },
      polygon,
      ground.heightAt,
      ground.config
    );
    triangles += builder.triangleCount - before;
  }
  return triangles;
}

/** Store the same fitted yard under the existing full source/input guards. */
export function bakeHogeHuisYard(
  ctx: YardContext & Pick<BuildContext, 'paving'>
): HogeHuisYardPlan {
  return { plan: preparedPlan(ctx), ground: hogeHuisGroundPolygons(ctx) };
}
